import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Check, X, CalendarClock, Users, Badge, Zap, BarChart, Layers, Cloud, BellRing, ExternalLink, ChevronRight, FileCheck, Shield, MessageSquare } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useAuth } from '../../contexts/AuthContext';

import NavBar from '../../components/NavBar';
import ResponsiveFooter from '../../components/ResponsiveFooter';

// Apple-inspired color palette (consistent with LandingPage)
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 24 }
  },
};

const fadeInUpVariant = {
  hidden: { y: 40, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1, 
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

// Pricing plans data with triple-pillar indicators
const pricingPlans = [
  {
    name: "Launch Pack",
    subtitle: "SMB Starter",
    price: null,
    priceText: "Custom",
    description: "Get started fast and prove value with your first contracts",
    timeline: "Ready in 5 minutes",
    features: [
      "Unlimited e-signatures (close deals faster)",
      "AI gist + risk flag summary (avoid costly mistakes)", 
      "Founder-led onboarding call (go live in 1 day)",
      "Basic contract templates",
      "Email support"
    ],
    pillars: {
      esignature: true,
      clm: true,
      ai: true,
    },
    highlight: false,
    cta: "Start Free Pilot",
    ctaSecondary: "Book Discovery Call",
    badge: "FASTEST START",
    outcome: "First signature today"
  },
  {
    name: "Pro Vertical Pack", 
    subtitle: "Industry-Tuned Solution",
    price: null,
    priceText: "Custom",
    description: "Customized for your industry with specialized workflows",
    timeline: "Customized in 2 weeks",
    features: [
      "Everything in Launch Pack",
      "Domain-specific clause library (top 10 for your industry)",
      "AI redlining tuned to your vertical (higher accuracy)",
      "1 native integration (pick your key system)",
      "Priority support & customization calls"
    ],
    pillars: {
      esignature: true,
      clm: true,
      ai: true,
    },
    highlight: true,
    cta: "Talk to Sales",
    ctaSecondary: "See Industry Examples", 
    badge: "RECOMMENDED",
    outcome: "87% faster contract processing"
  },
  {
    name: "Scale",
    subtitle: "Enterprise-Ready Growth",
    price: null,
    priceText: "Custom", 
    description: "Advanced governance and controls for scaling organizations",
    timeline: "Enterprise deployment in 30 days",
    features: [
      "Everything in Pro Vertical Pack",
      "SSO + advanced governance (enterprise security)",
      "Multi-workspace & audit logs (compliance ready)",
      "Dedicated Customer Success Manager",
      "Custom integrations & API access"
    ],
    pillars: {
      esignature: true,
      clm: true,  
      ai: true,
    },
    highlight: false,
    cta: "Book Discovery Call",
    ctaSecondary: "View Enterprise Features",
    badge: "FULL CONTROL",
    outcome: "Enterprise compliance + scale"
  }
];

// Feature categories for comparison - simplified
const featureCategories = [
  {
    id: "aiReview",
    name: "AI Contract Review",
    description: "AI analysis of contracts"
  },
  {
    id: "signature",
    name: "E-Signature",
    description: "Document signing"
  },
  {
    id: "clm",
    name: "CLM Features",
    description: "Contract lifecycle management"
  },
  {
    id: "vendorManagement",
    name: "Vendor Management",
    description: "Vendor organization"
  },
  {
    id: "templates",
    name: "Templates",
    description: "Contract templates"
  },
  {
    id: "userMin",
    name: "Minimum Users",
    description: "User requirement"
  },
  {
    id: "payAsYouGo",
    name: "Pay-As-You-Go",
    description: "Flexible payment"
  },
  {
    id: "startupDiscount",
    name: "Startup Program",
    description: "Startup pricing"
  }
];



// FAQ data
const faqData = [
  {
    question: "How do you determine pricing?",
    answer: "We work with each customer to understand their specific needs, usage patterns, and budget. Our pricing is based on the value we deliver to your business, not arbitrary feature tiers."
  },
  {
    question: "What's included in the pilot program?",
    answer: "Our pilot program includes full access to our platform, dedicated onboarding support, and direct access to our team. We'll work closely with you to ensure the solution fits your workflow perfectly."
  },
  {
    question: "How do you customize solutions for different industries?",
    answer: "We've learned that every industry has unique contract needs. During our discovery process, we identify your specific requirements and customize our AI models, templates, and workflows accordingly."
  },
  {
    question: "What support do you provide during onboarding?",
    answer: "Every customer gets dedicated onboarding with our founders. We'll sit with you during your first few contract reviews, help set up your templates, and ensure your team is comfortable with the platform."
  },
  {
    question: "Can I change or cancel my plan later?",
    answer: "Absolutely. We believe in building long-term partnerships, not locking you into contracts. You can adjust your plan or cancel at any time as your needs evolve."
  }
];

const PlanComparison = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const controls = useAnimation();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Intersection Observer hooks for scroll animations
  const [heroRef, heroInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [pricingRef, pricingInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  

  
  useEffect(() => {
    if (heroInView) {
      controls.start('visible');
    }
  }, [controls, heroInView]);
  
  useEffect(() => {
    if (pricingInView) {
      controls.start('visible');
    }
  }, [controls, pricingInView]);
  

  
  const handleTrialClick = (plan) => {
    window.open('https://cal.com/ranvirdeshmukh/book-a-demo-signpact', '_blank');
  };
  
  const toggleFaq = (index) => {
    if (activeFaq === index) {
      setActiveFaq(null);
    } else {
      setActiveFaq(index);
    }
  };
  
  return (
    <div className="min-h-screen bg-white flex flex-col overflow-hidden">
      {/* Navigation */}
      <NavBar 
        activePage=""
      />
      
      {/* Hero Section */}
      <motion.div 
        className="pt-40 pb-20 relative"
        ref={heroRef}
        style={{
          background: `radial-gradient(circle at 70% 30%, ${colors.accentLight} 0%, rgba(255, 255, 255, 0) 25%)`,
        }}
      >
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center max-w-4xl mx-auto mb-16"
            variants={containerVariants}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
          >
            <motion.div
              variants={itemVariants}
              className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" 
              style={{backgroundColor: colors.accentLight, color: colors.accent}}
            >
              Feature Comparison
            </motion.div>
            <motion.h1 
              variants={itemVariants}
              className="text-5xl md:text-6xl font-bold mb-8"
              style={{letterSpacing: '-0.02em'}}
            >
              Compare plans and <span style={{color: colors.accent}}>features.</span>
            </motion.h1>
            <motion.p 
              variants={itemVariants}
              className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto font-light"
              style={{color: colors.gray700, lineHeight: '1.5'}}
            >
              Detailed comparison of our plans and how we stack up against competitors.
            </motion.p>
            
            {/* Three pillars section */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16 mb-12"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{backgroundColor: colors.accentLight}}>
                  <FileCheck style={{color: colors.accent}} size={22} />
                </div>
                <h3 className="text-lg font-semibold mb-1">E-Signature</h3>
                <p className="text-sm" style={{color: colors.gray700}}>Legally binding signatures</p>
              </div>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{backgroundColor: colors.accentLight}}>
                  <Shield style={{color: colors.accent}} size={22} />
                </div>
                <h3 className="text-lg font-semibold mb-1">CLM</h3>
                <p className="text-sm" style={{color: colors.gray700}}>Complete contract management</p>
              </div>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{backgroundColor: colors.accentLight}}>
                  <Zap style={{color: colors.accent}} size={22} />
                </div>
                <h3 className="text-lg font-semibold mb-1">AI Analysis</h3>
                <p className="text-sm" style={{color: colors.gray700}}>Smart risk detection</p>
              </div>
            </motion.div>
            

            
            
          </motion.div>
          
          {/* Pricing Cards */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
            ref={pricingRef}
          >
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                className={`rounded-xl border p-8 flex flex-col relative ${
                  plan.highlight ? 'ring-2 ring-blue-500 shadow-lg' : 'shadow-sm'
                }`}
                style={{
                  borderColor: plan.highlight ? colors.accent : colors.gray200,
                  backgroundColor: colors.white
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={pricingInView ? { 
                  opacity: 1, 
                  y: 0, 
                  transition: { 
                    delay: index * 0.1 + 0.3,
                    duration: 0.6,
                    ease: [0.6, -0.05, 0.01, 0.99]
                  } 
                } : { opacity: 0, y: 30 }}
                whileHover={{ 
                  y: -8,
                  boxShadow: "0 20px 30px rgba(0, 0, 0, 0.08)",
                  transition: { 
                    type: "spring", 
                    stiffness: 200, 
                    damping: 25 
                  }
                }}
              >
                {plan.badge && (
                  <div 
                    className="absolute -top-3 right-8 text-xs font-semibold py-1 px-3 rounded-full shadow-sm"
                    style={{
                      backgroundColor: plan.highlight ? colors.accent : colors.gray50,
                      color: plan.highlight ? colors.white : colors.gray700,
                    }}
                  >
                    {plan.badge}
                  </div>
                )}
                
                <div className="flex items-center mb-6">
                  {Object.entries(plan.pillars).map(([key, enabled], i) => (
                    enabled && (
                      <div 
                        key={key} 
                        className={`w-6 h-6 rounded-full flex items-center justify-center ${i > 0 ? '-ml-2' : ''}`}
                        style={{
                          backgroundColor: i === 0 ? colors.accentLight : i === 1 ? colors.successLight : colors.warningLight,
                          color: i === 0 ? colors.accent : i === 1 ? colors.success : colors.warning,
                          zIndex: 3 - i
                        }}
                      >
                        {i === 0 && <FileCheck size={12} />}
                        {i === 1 && <Shield size={12} />}
                        {i === 2 && <Zap size={12} />}
                      </div>
                    )
                  ))}
                  <div className="ml-2 text-xs font-medium" style={{color: colors.gray500}}>All-in-one solution</div>
                </div>
                
                <div className="mb-4">
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <p className="text-sm font-medium text-blue-600">{plan.subtitle}</p>
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold">{plan.priceText || `$${plan.price}`}</span>
                    {plan.price && (
                      <span className="text-sm ml-1 text-gray-500">/mo per user</span>
                    )}
                  </div>
                  <div className="mb-4">
                    <p className="text-sm mb-2" style={{color: colors.gray500}}>{plan.description}</p>
                    <div className="flex items-center text-xs font-medium" style={{color: colors.accent}}>
                      <CalendarClock size={12} className="mr-1" />
                      {plan.timeline}
                    </div>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <Check size={16} className="mr-2 mt-1 flex-shrink-0" style={{color: colors.accent}} />
                      <span className="text-sm" style={{color: colors.gray700}}>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {plan.outcome && (
                  <div className="mb-6 p-3 rounded-lg" style={{backgroundColor: colors.successLight}}>
                    <div className="text-sm font-medium" style={{color: colors.success}}>
                      Expected Outcome: {plan.outcome}
                    </div>
                  </div>
                )}
                
                <div className="space-y-3 mt-auto">
                  <motion.button 
                    onClick={() => handleTrialClick(plan)}
                    className={`w-full py-3 rounded-full font-medium text-center transition shadow-sm ${plan.highlight ? 'text-white' : 'border'}`}
                    style={{
                      backgroundColor: plan.highlight ? colors.accent : 'transparent',
                      borderColor: plan.highlight ? 'transparent' : colors.gray200,
                      color: plan.highlight ? colors.white : colors.gray900
                    }}
                    whileHover={{ 
                      scale: 1.03, 
                      boxShadow: plan.highlight ? "0 10px 15px -3px rgba(37, 99, 235, 0.1), 0 4px 6px -2px rgba(37, 99, 235, 0.05)" : "none",
                      backgroundColor: plan.highlight ? colors.accent : colors.gray50
                    }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {plan.cta}
                  </motion.button>
                  
                  {plan.ctaSecondary && (
                    <button 
                      onClick={() => handleTrialClick(plan)}
                      className="w-full py-2 text-sm text-center font-medium transition-colors"
                      style={{color: colors.gray700}}
                      onMouseOver={(e) => {e.currentTarget.style.color = colors.accent}}
                      onMouseOut={(e) => {e.currentTarget.style.color = colors.gray700}}
                    >
                      {plan.ctaSecondary}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        <motion.div 
          className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full opacity-10 blur-3xl" 
          style={{backgroundColor: colors.accent}}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 8,
            repeatType: "mirror"
          }}
        />
        <motion.div 
          className="absolute -top-16 -left-16 w-64 h-64 rounded-full opacity-10 blur-3xl" 
          style={{backgroundColor: colors.accent}}
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.12, 0.1]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 10,
            repeatType: "mirror",
            delay: 2
          }}
        />
      </motion.div>
      
            
      {/* Feature Comparison Section - full competitor table from original pricing page */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center mb-14"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.div 
              className="inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6" 
              style={{
                backgroundColor: colors.accentLight, 
                color: colors.accent,
                fontWeight: 500
              }}
              whileHover={{ 
                scale: 1.02,
                backgroundColor: `rgba(37, 99, 235, 0.15)` 
              }}
            >
              Feature Comparison
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{letterSpacing: '-0.04em', lineHeight: '1.1'}}>
              See how we stack up against<br /> the competition
            </h2>
            <p className="text-xl max-w-3xl mx-auto" style={{color: colors.gray700, lineHeight: '1.5'}}>
              SignPact offers the perfect balance of features, flexibility, and affordability
            </p>
          </motion.div>
          
          <motion.div
            className="max-w-6xl mx-auto overflow-hidden rounded-2xl shadow-lg border" 
            style={{
              borderColor: 'rgba(226, 232, 240, 0.6)',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06), 0 0 10px rgba(0, 0, 0, 0.02)'
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            whileHover={{ 
              y: -5,
              transition: { duration: 0.3 }
            }}
          >
            {/* Table Header Bar - Similar to Document Generator Header */}
            <div className="h-12 flex items-center px-5 border-b" 
                style={{
                  background: 'linear-gradient(to right, #f9fafc, #f5f7fa)',
                  borderColor: 'rgba(226, 232, 240, 0.6)'
                }}>
              <div className="flex space-x-1.5 mr-5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
              </div>
              <div className="text-xs font-medium flex items-center" style={{color: colors.gray600}}>
                <BarChart size={12} className="mr-1.5 text-blue-600" />
                Product Comparison
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full bg-white border-collapse">
                <thead>
                  <tr>
                    <th className="p-4 text-left bg-gradient-to-br from-gray-50 to-gray-100 border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}></th>
                    
                    <th className="p-5 text-center border-b relative overflow-hidden" style={{
                      background: 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.08), rgba(59, 130, 246, 0.08))',
                      borderColor: 'rgba(226, 232, 240, 0.7)',
                    }}>
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500"></div>
                      <div className="font-medium">SignPact</div>
                      <div className="text-sm mt-1 text-blue-700">Custom</div>
                      <div className="text-xs mt-1.5 font-normal rounded-full px-2 py-0.5 inline-block bg-white/70 text-blue-600">
                        Best Value
                      </div>
                    </th>
                    
                    <th className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="font-medium">DocuSign</div>
                      <div className="text-sm text-gray-600 mt-1">$40/mo</div>
                    </th>
                    
                    <th className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="font-medium">Ironclad</div>
                      <div className="text-sm text-gray-600 mt-1">$25/mo</div>
                    </th>
                    
                    <th className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="font-medium">Evisort</div>
                      <div className="text-sm text-gray-600 mt-1">$250/mo</div>
                    </th>
                    
                    <th className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="font-medium">PandaDoc</div>
                      <div className="text-sm text-gray-600 mt-1">$22/mo</div>
                    </th>
                    
                    <th className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="font-medium">Juro</div>
                      <div className="text-sm text-gray-600 mt-1">$37/mo</div>
                    </th>
                  </tr>
                </thead>
                
                <tbody>
                  {/* AI Contract Review */}
                  <tr>
                    <td className="p-4 font-medium bg-gradient-to-r from-gray-50 to-transparent border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="flex items-center">
                        <div className="mr-3 h-8 w-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                          <Zap size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <div>AI Contract Review</div>
                          <div className="text-xs text-gray-500">AI analysis of contracts</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{
                      background: 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.08), rgba(59, 130, 246, 0.08))',
                      borderColor: 'rgba(226, 232, 240, 0.7)'
                    }}>
                      <div className="flex justify-center">
                        <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-sm font-medium flex items-center">
                          <Check size={14} className="mr-1" />
                          Advanced AI
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        Limited
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center opacity-70">
                        <Check size={14} className="mr-1" />
                        Basic
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Enterprise-grade
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        No
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center opacity-70">
                        <Check size={14} className="mr-1" />
                        Basic
                      </div>
                    </td>
                  </tr>
                  
                  {/* E-Signature */}
                  <tr>
                    <td className="p-4 font-medium bg-gradient-to-r from-gray-50 to-transparent border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="flex items-center">
                        <div className="mr-3 h-8 w-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                          <FileCheck size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <div>E-Signature</div>
                          <div className="text-xs text-gray-500">Document signing</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{
                      background: 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.08), rgba(59, 130, 246, 0.08))',
                      borderColor: 'rgba(226, 232, 240, 0.7)'
                    }}>
                      <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Built-in
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Industry standard
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        Requires integration
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        Requires integration
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center opacity-80">
                        <Check size={14} className="mr-1" />
                        Basic
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Built-in
                      </div>
                    </td>
                  </tr>
                  
                  {/* CLM Features */}
                  <tr>
                    <td className="p-4 font-medium bg-gradient-to-r from-gray-50 to-transparent border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="flex items-center">
                        <div className="mr-3 h-8 w-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                          <Shield size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <div>CLM Features</div>
                          <div className="text-xs text-gray-500">Contract lifecycle management</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{
                      background: 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.08), rgba(59, 130, 246, 0.08))',
                      borderColor: 'rgba(226, 232, 240, 0.7)'
                    }}>
                      <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Complete
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Advanced
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Enterprise-focused
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Enterprise-grade
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        Limited
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Modern
                      </div>
                    </td>
                  </tr>
                  
                  {/* Vendor Management */}
                  <tr>
                    <td className="p-4 font-medium bg-gradient-to-r from-gray-50 to-transparent border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="flex items-center">
                        <div className="mr-3 h-8 w-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                          <Users size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <div>Vendor Management</div>
                          <div className="text-xs text-gray-500">Vendor organization</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{
                      background: 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.08), rgba(59, 130, 246, 0.08))',
                      borderColor: 'rgba(226, 232, 240, 0.7)'
                    }}>
                      <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Dedicated dashboards
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Basic
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Complex
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Advanced
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        No
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Limited
                      </div>
                    </td>
                  </tr>
                  
                  {/* Templates */}
                  <tr>
                    <td className="p-4 font-medium bg-gradient-to-r from-gray-50 to-transparent border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="flex items-center">
                        <div className="mr-3 h-8 w-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                          <Layers size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <div>Templates</div>
                          <div className="text-xs text-gray-500">Contract templates</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{
                      background: 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.08), rgba(59, 130, 246, 0.08))',
                      borderColor: 'rgba(226, 232, 240, 0.7)'
                    }}>
                      <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        AI-powered
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Standard
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Complex
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Limited
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Basic
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        Standard
                      </div>
                    </td>
                  </tr>
                  
                  {/* Minimum Users */}
                  <tr>
                    <td className="p-4 font-medium bg-gradient-to-r from-gray-50 to-transparent border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="flex items-center">
                        <div className="mr-3 h-8 w-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                          <Users size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <div>Minimum Users</div>
                          <div className="text-xs text-gray-500">User requirement</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{
                      background: 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.08), rgba(59, 130, 246, 0.08))',
                      borderColor: 'rgba(226, 232, 240, 0.7)'
                    }}>
                      <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-sm font-medium inline-flex items-center">
                        <span className="text-sm">1 Single user</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <span className="text-sm">3+ users</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <span className="text-sm">Custom</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <span className="text-sm">Enterprise</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <span className="text-sm">1 Single user</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <span className="text-sm">Custom</span>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Pay-As-You-Go */}
                  <tr>
                    <td className="p-4 font-medium bg-gradient-to-r from-gray-50 to-transparent border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="flex items-center">
                        <div className="mr-3 h-8 w-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                          <MessageSquare size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <div>Pay-As-You-Go</div>
                          <div className="text-xs text-gray-500">Flexible payment</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{
                      background: 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.08), rgba(59, 130, 246, 0.08))',
                      borderColor: 'rgba(226, 232, 240, 0.7)'
                    }}>
                      <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        <span className="text-sm">Available</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        <span className="text-sm text-gray-500">Annual only</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        <span className="text-sm text-gray-500">No</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        <span className="text-sm text-gray-500">Annual only</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        <span className="text-sm">Available</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        <span className="text-sm text-gray-500">No</span>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Startup Program */}
                  <tr>
                    <td className="p-4 font-medium bg-gradient-to-r from-gray-50 to-transparent border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="flex items-center">
                        <div className="mr-3 h-8 w-8 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
                          <Zap size={16} className="text-blue-600" />
                        </div>
                        <div>
                          <div>Startup Program</div>
                          <div className="text-xs text-gray-500">Startup pricing</div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{
                      background: 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.08), rgba(59, 130, 246, 0.08))',
                      borderColor: 'rgba(226, 232, 240, 0.7)'
                    }}>
                      <div className="px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-sm font-medium inline-flex items-center">
                        <Check size={14} className="mr-1" />
                        <span className="text-sm">Available</span>
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        No
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        No
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        No
                      </div>
                    </td>
                    
                    <td className="p-4 text-center border-b" style={{borderColor: 'rgba(226, 232, 240, 0.7)'}}>
                      <div className="px-3 py-1.5 bg-gray-100 text-gray-500 rounded-full text-sm font-medium inline-flex items-center">
                        <X size={14} className="mr-1" />
                        No
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>
          
          {/* Key advantages - simplified */}
          <div className="mt-16 grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            <motion.div
              className="bg-white p-5 rounded-xl shadow-sm border"
              style={{borderColor: colors.gray200}}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              whileHover={{ 
                y: -3,
                boxShadow: "0 10px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <h3 className="font-bold flex items-center">
                <Zap size={18} className="mr-2" style={{color: colors.accent}} />
                AI Analysis
              </h3>
              <p className="text-xs mt-2" style={{color: colors.gray700}}>Advanced AI review vs. basic or no AI from competitors</p>
            </motion.div>
            
            <motion.div
              className="bg-white p-5 rounded-xl shadow-sm border"
              style={{borderColor: colors.gray200}}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ 
                y: -3,
                boxShadow: "0 10px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <h3 className="font-bold flex items-center">
                <FileCheck size={18} className="mr-2" style={{color: colors.accent}} />
                All-In-One
              </h3>
              <p className="text-xs mt-2" style={{color: colors.gray700}}>Built-in e-signatures vs. additional integrations required</p>
            </motion.div>
            
            <motion.div
              className="bg-white p-5 rounded-xl shadow-sm border"
              style={{borderColor: colors.gray200}}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ 
                y: -3,
                boxShadow: "0 10px 20px rgba(0, 0, 0, 0.05)",
              }}
            >
              <h3 className="font-bold flex items-center">
                <Shield size={18} className="mr-2" style={{color: colors.accent}} />
                Startup Friendly
              </h3>
              <p className="text-xs mt-2" style={{color: colors.gray700}}>Only provider with startup plan & flexible pricing</p>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-2 rounded-full text-sm font-medium mb-4" style={{backgroundColor: colors.accentLight, color: colors.accent}}>
              FAQ
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{letterSpacing: '-0.02em'}}>
              Frequently asked questions
            </h2>
            <p className="text-xl" style={{color: colors.gray700, lineHeight: '1.5'}}>
              Everything you need to know about our pricing and plans
            </p>
          </motion.div>
          
          <div className="max-w-3xl mx-auto">
            {faqData.map((faq, index) => (
              <motion.div 
                key={index} 
                className="mb-4 bg-white rounded-xl overflow-hidden shadow-sm"
                style={{
                  borderLeft: activeFaq === index ? `3px solid ${colors.accent}` : '3px solid transparent',
                  transform: activeFaq === index ? 'translateX(5px)' : 'translateX(0)'
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ 
                  boxShadow: "0 5px 15px rgba(0, 0, 0, 0.05)",
                  transition: { duration: 0.2 }
                }}
              >
                <motion.button 
                  className="w-full text-left p-5 flex justify-between items-center font-medium"
                  onClick={() => toggleFaq(index)}
                  style={{color: activeFaq === index ? colors.accent : colors.gray900}}
                  whileHover={{ color: colors.accent }}
                >
                  {faq.question}
                  <motion.div
                    animate={{ 
                      rotate: activeFaq === index ? 90 : 0,
                      backgroundColor: activeFaq === index ? colors.accentLight : colors.gray50
                    }}
                    transition={{ duration: 0.3 }}
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  >
                    <ChevronRight 
                      size={14} 
                      style={{
                        color: activeFaq === index ? colors.accent : colors.gray500
                      }} 
                    />
                  </motion.div>
                </motion.button>
                
                <motion.div 
                  className="overflow-hidden transition-all duration-300"
                  initial={false}
                  animate={{ 
                    height: activeFaq === index ? 'auto' : 0,
                    opacity: activeFaq === index ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <p className="px-5 pb-5 text-sm" style={{color: colors.gray700, lineHeight: '1.6'}}>
                    {faq.answer}
                  </p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
      
      {/* CTA Section */}
      <div className="py-32 relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            className="rounded-2xl p-16 max-w-5xl mx-auto shadow-xl text-center relative"
            style={{background: 'linear-gradient(125deg, rgba(30, 64, 175, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)'}}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            whileHover={{ 
              boxShadow: "0 30px 60px rgba(0, 0, 0, 0.15)",
              y: -5,
              transition: { type: "spring", stiffness: 200, damping: 25 }
            }}
          >
            <div className="relative z-10">
              <motion.h2 
                className="text-3xl md:text-4xl font-bold text-white mb-6" 
                style={{letterSpacing: '-0.02em'}}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Ready to see how SignPact compares?
              </motion.h2>
              <motion.p 
                className="text-xl mb-10 max-w-3xl mx-auto" 
                style={{color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.5'}}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Book a discovery call to see our platform in action and get a custom quote.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <motion.button 
                  onClick={() => window.open('https://cal.com/ranvirdeshmukh/book-a-demo-signpact', '_blank')}
                  className="px-10 py-4 bg-white rounded-full font-medium hover:bg-gray-50 transition shadow-md inline-flex items-center"
                  style={{color: colors.accent}}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0 15px 25px rgba(0, 0, 0, 0.1)"
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  Book Discovery Call
                  <motion.div
                    className="ml-2"
                    animate={{ x: [0, 4, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5, repeatType: "mirror" }}
                  >
                    <ArrowRight size={18} />
                  </motion.div>
                </motion.button>
              </motion.div>
              
              <motion.div 
                className="mt-10 flex flex-wrap justify-center space-x-3 md:space-x-8" 
                style={{color: 'rgba(255, 255, 255, 0.8)'}}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <motion.div 
                  className="flex items-center mb-3 md:mb-0"
                  whileHover={{ scale: 1.05, color: 'rgba(255, 255, 255, 1)' }}
                >
                  <CalendarClock size={18} className="mr-2" />
                  <span>Custom demo</span>
                </motion.div>
                <motion.div 
                  className="flex items-center mb-3 md:mb-0"
                  whileHover={{ scale: 1.05, color: 'rgba(255, 255, 255, 1)' }}
                >
                  <Badge size={18} className="mr-2" />
                  <span>Personalized quote</span>
                </motion.div>
                <motion.div 
                  className="flex items-center"
                  whileHover={{ scale: 1.05, color: 'rgba(255, 255, 255, 1)' }}
                >
                  <Cloud size={18} className="mr-2" />
                  <span>No commitment</span>
                </motion.div>
              </motion.div>
            </div>
            
            {/* Decorative elements */}
            <motion.div 
              className="absolute top-0 right-0 w-80 h-80 rounded-full opacity-30 blur-3xl" 
              style={{backgroundColor: 'white'}}
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.25, 0.2]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 8,
                repeatType: "mirror"
              }}
            />
            <motion.div 
              className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-30 blur-3xl" 
              style={{backgroundColor: 'white'}}
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.2, 0.15, 0.2]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 10,
                repeatType: "mirror",
                delay: 2
              }}
            />
          </motion.div>
        </div>
        
        {/* Background decoration */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{backgroundColor: colors.accent}}></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{backgroundColor: colors.accent}}></div>
      </div>
      
      {/* Footer */}
      <ResponsiveFooter activePage="" />
      
    </div>
  );
};

export default PlanComparison; 